/**
 * ImportBatchDetailPage — full status, queue progress, error log.
 *
 * Polls the status endpoint every 5 seconds while the batch is non-terminal.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, RefreshCw, Zap } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import importService from '../services/importService';
import { BatchStatusResponse } from '../types/importBatch';

const TERMINAL = new Set(['complete', 'failed']);

const ImportBatchDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const batchId = Number(id);
  const navigate = useNavigate();
  const [batch, setBatch] = useState<BatchStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!batchId) return;
    const res = await importService.getStatus(batchId);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    setBatch(res.data || null);
  };

  useEffect(() => {
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;
    const init = async () => {
      setLoading(true);
      await refresh();
      if (cancelled) return;
      setLoading(false);
      interval = setInterval(async () => {
        if (cancelled) return;
        const res = await importService.getStatus(batchId);
        if (res.data) {
          setBatch(res.data);
          if (TERMINAL.has(res.data.status) && interval) {
            clearInterval(interval);
            interval = null;
          }
        }
      }, 5000);
    };
    init();
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const handleBumpHigh = async () => {
    const res = await importService.bumpPriority(batchId, 1);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Priority raised to High');
    refresh();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center p-10 text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" /> Loading batch...
        </div>
      </DashboardLayout>
    );
  }

  if (!batch) {
    return (
      <DashboardLayout>
        <p className="text-gray-600">Batch not found.</p>
      </DashboardLayout>
    );
  }

  const totalQueued = batch.research_queued || 0;
  const completeCount = batch.research_complete || 0;
  const progressPct =
    totalQueued > 0 ? Math.min(100, Math.round((completeCount / totalQueued) * 100)) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/imports')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to imports
          </button>
          <div className="flex gap-2">
            <button
              onClick={refresh}
              className="inline-flex items-center px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </button>
            <button
              onClick={handleBumpHigh}
              className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Zap className="w-4 h-4 mr-1" /> Bump priority to High
            </button>
          </div>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Batch #{batch.id} — {batch.source_file || batch.source_type}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Status:{' '}
            <span className="font-medium uppercase">{batch.status}</span>{' '}
            • Created{' '}
            {batch.created_at
              ? new Date(batch.created_at).toLocaleString()
              : ''}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Total" value={batch.total_records} />
          <Stat label="Imported" value={batch.imported_records} />
          <Stat label="Duplicates" value={batch.duplicate_records} />
          <Stat label="Errors" value={batch.error_records} />
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Auto-research queue
          </h2>
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>
              {completeCount} / {totalQueued} complete
            </span>
            <span>{progressPct}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {batch.estimated_completion && (
            <p className="text-xs text-gray-500">
              Estimated completion:{' '}
              {new Date(batch.estimated_completion).toLocaleString()}
            </p>
          )}
        </div>

        {batch.tags && batch.tags.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Tags</h3>
            <div className="flex flex-wrap gap-1">
              {batch.tags.map((t) => (
                <span
                  key={t}
                  className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {batch.error_log && batch.error_log.length > 0 && (
          <div className="bg-white border border-red-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-red-700 mb-2">
              Error log ({batch.error_log.length})
            </h3>
            <ul className="text-xs text-red-700 space-y-1 max-h-64 overflow-auto">
              {batch.error_log.map((err: any, i: number) => (
                <li key={i}>
                  Row {err.row}: {err.error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const Stat: React.FC<{ label: string; value?: number }> = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="text-xs text-gray-500 uppercase">{label}</div>
    <div className="text-2xl font-bold text-gray-900">{value ?? 0}</div>
  </div>
);

export default ImportBatchDetailPage;
